import { Inject, Injectable, Optional } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { IActivityApi } from '../api/interfaces/activity-abstract.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { httpActivitySessionToSession } from '../helpers/activity.helper';
import { Activities } from '../models/activity.model';
import { StatSportType } from '../models/stats.model';

// 3 minutes
const CACHE_VALIDITY = 180000;

export type ActivitiesMonth = {
  [key in StatSportType]: {
    activities: Activities;
    loading: boolean;
    loaded: boolean;
  };
};

export interface ActivitiesByMonthState {
  activities: {
    // Keys in format 'MONTH_NUMBER-YEAR'
    [key: string]: ActivitiesMonth;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ActivitiesByMonthFacade {
  private cacheValidityTime = 0;
  private state: ActivitiesByMonthState = {
    activities: {},
  };
  private readonly store = new BehaviorSubject<ActivitiesByMonthState>(
    this.state
  );
  private readonly state$ = this.store.asObservable();
  private readonly subscriptions = new Subscription();

  public readonly activities$ = this.state$.pipe(
    map((state) => state.activities),
    distinctUntilChanged()
  );

  constructor(
    private readonly activityApi: IActivityApi,
    @Inject(NEVER_TOKEN) @Optional() public userId: number
  ) {}

  getState$(): Observable<ActivitiesByMonthState> {
    return this.state$;
  }

  getState(): ActivitiesByMonthState {
    return this.state;
  }

  private setState(newState: Partial<ActivitiesByMonthState>) {
    this.state = { ...this.state, ...newState };
    this.store.next(this.state);
  }

  reset(): void {
    this.setState({
      activities: {},
    });
  }

  loadActivitiesByMonth(
    month: number,
    year: number,
    sportType: StatSportType,
    limit?: number,
    page?: number,
    refresh: boolean = false
  ): void {
    const monthKey = this.getMonthKey(month, year);
    const activitiesMonth = this.getMonthByKey(monthKey);
    // TODO manage cache in loaded case !

    if (
      !refresh &&
      (activitiesMonth[sportType].loaded || activitiesMonth[sportType].loading)
    ) {
      return;
    }

    activitiesMonth[sportType].loading = true;
    this.setState({
      activities: {
        ...this.state.activities,
      },
    });
    const sub = this.activityApi
      .getActivitiesByMonth(month, year, sportType, limit, page, this.userId)
      .subscribe({
        next: this.receiveActivityMonth.bind(this, monthKey, sportType),
        error: this.errorOnReceivedActivityMonth.bind(
          this,
          monthKey,
          sportType
        ),
      });
    this.subscriptions.add(sub);
  }

  private receiveActivityMonth(
    monthKey: string,
    sportType: StatSportType,
    { data: activitiesHttp }
  ): void {
    const activities = activitiesHttp.map(httpActivitySessionToSession);
    const activitiesMonth = this.getMonthByKey(monthKey);
    activitiesMonth[sportType] = {
      loading: false,
      loaded: true,
      activities,
    };
    this.setState({
      activities: {
        ...this.state.activities,
      },
    });
  }

  private errorOnReceivedActivityMonth(
    monthKey: string,
    sportType: StatSportType,
    error: any
  ) {
    const activitiesMonth = this.getMonthByKey(monthKey);
    activitiesMonth[sportType].loading = false;
    this.setState({
      activities: {
        ...this.state.activities,
      },
    });
  }

  isMonthLoading(month: number, year: number): boolean {
    return this.state[this.getMonthKey(month, year)]?.loading;
  }

  getMonthByKey(monthKey: string) {
    let activitiesMonth = this.state.activities[monthKey];
    // Generate it if does not exist
    if (!activitiesMonth) {
      activitiesMonth = this.generateEmptyMonth();
      this.state.activities[monthKey] = activitiesMonth;
    }
    return activitiesMonth;
  }

  private generateEmptyMonth(): ActivitiesMonth {
    return {
      [StatSportType.ALL]: {
        activities: [],
        loading: false,
        loaded: false,
      },
      [StatSportType.CYCLING]: {
        activities: [],
        loading: false,
        loaded: false,
      },
      [StatSportType.RUNNING]: {
        activities: [],
        loading: false,
        loaded: false,
      },
      [StatSportType.ROWING]: {
        activities: [],
        loading: false,
        loaded: false,
      },
    };
  }

  getMonthKey(month: number, year: number) {
    return `${month}-${year}`;
  }

  isCacheValid() {
    return this.cacheValidityTime > Date.now();
  }

  onDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
